import {useEffect, useMemo, useState} from "react";
import {Box, Button, Stack, Typography} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {Navigate, useNavigate, useParams, useSearchParams} from "react-router";
import {appSession} from "../../../TR3AD.ts";
import WidgetHeader from "../../../widgets/WidgetHeader.tsx";
import {appStyles} from "../../../AppStyles.ts";
import {StyledInputBase, StyledSearch, StyledSearchIconWrapper} from "../../AppStyledComponents.tsx";
import ResizableSplit from "../../common/ResizableSplit.tsx";

// Lightweight shell record — full Researcher SDO still TBD per research feature plan.
interface SampleResearcher {
    key: string;
    name: string;
    description: string;
}

// TODO: Replace with API call to load researchers for the network
const SAMPLE_RESEARCHERS: SampleResearcher[] = [
    {key: "threat-enrichment", name: "Threat Enrichment Researcher", description: "Enriches threat profiles with open-source indicators and known-bad feeds."},
    {key: "osint-collector", name: "Open Source Collector", description: "Collects and correlates open-source reporting against the active network."},
    {key: "network-activity", name: "Network Activity Analyst", description: "Analyzes network activity over time for patterns of interest."},
    {key: "geo-cross-ref", name: "Geo Cross-Reference", description: "Cross-references profiles against geographic signals and location feeds."},
];

const researcherCardSx = (selected: boolean) => ({
    background: "linear-gradient(135deg, rgba(9,25,32,0.85) 0%, rgba(20,40,55,0.7) 100%)",
    border: selected ? "1px solid #38ff82" : "1px solid #3F464F",
    borderRadius: "10px",
    padding: "12px 16px",
    width: "100%",
    cursor: "pointer",
    transition: "border-color 0.15s, background 0.15s",
    "&:hover": {
        borderColor: selected ? "#38ff82" : "rgba(56, 255, 130, 0.45)",
        backgroundColor: "rgba(56, 255, 130, 0.04)",
    },
});

function UXResearcher() {
    const {networkUID} = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // TODO: Remove `?demo=empty` dev toggle once researchers load from the API.
    // Use it to preview the "no researchers configured" state without editing code.
    const researchers = searchParams.get("demo") === "empty" ? [] : SAMPLE_RESEARCHERS;

    const [filterText, setFilterText] = useState("");
    const [selectedKey, setSelectedKey] = useState<string | null>(null);

    const filtered = useMemo(() => {
        const q = filterText.trim().toLowerCase();
        if (!q) return researchers;
        return researchers.filter(r =>
            r.name.toLowerCase().includes(q) || r.description.toLowerCase().includes(q)
        );
    }, [filterText, researchers]);

    // §21 split-pane selection behavior — keep selection within the filtered set, auto-select first.
    useEffect(() => {
        if (filtered.length === 0) {
            if (selectedKey !== null) setSelectedKey(null);
            return;
        }
        const stillVisible = filtered.some(r => r.key === selectedKey);
        if (!stillVisible) setSelectedKey(filtered[0].key);
    }, [filtered, selectedKey]);

    const selected = useMemo(() => {
        if (!selectedKey) return null;
        return filtered.find(r => r.key === selectedKey) ?? null;
    }, [filtered, selectedKey]);

    function handleCreate() {
        navigate(`/networks/${networkUID}/research/create`);
    }

    function handleOpenResearcher() {
        if (!selected) return;
        navigate(`/networks/${networkUID}/research/${selected.key}`, {state: {researcher: selected}});
    }

    function handleOpenResults() {
        if (!selected) return;
        navigate(`/networks/${networkUID}/research/${selected.key}/results`, {state: {researcher: selected}});
    }

    return (
        <Stack direction="column" width="100%" minHeight="100vh">
            {!appSession.activeSession() ? (
                <Navigate to="/" />
            ) : (
                <Stack direction="column" spacing={1} width="99%">
                    <WidgetHeader
                        title="Researchers"
                        breadcrumbs={[
                            {display: "Networks", linkTo: "/networks/", activeView: false},
                            {display: String(networkUID), linkTo: `/networks/${networkUID}`, activeView: false},
                            {display: "Researchers", linkTo: "", activeView: true},
                        ]}
                    />
                    <Stack sx={appStyles.sectionBox} spacing={0} width="100%">
                        {/* Title */}
                        <Stack direction="row" padding={1} justifyContent="center">
                            <Typography sx={appStyles.panelSectionTitle}>Researchers</Typography>
                        </Stack>
                        {/* Split pane — resizable, always renders both sides (25/75 default) */}
                        <Box sx={{display: "flex", padding: "8px 12px", alignItems: "stretch", minHeight: 400}}>
                            <ResizableSplit
                                direction="horizontal"
                                defaultRatio={0.25}
                                minRatio={0.15}
                                maxRatio={0.6}
                                first={(
                                    <Box sx={{display: "flex", flexDirection: "column", width: "100%", height: "100%", border: "1px solid #3F464F", borderRadius: "8px", padding: "12px", background: "rgba(15, 15, 15, 0.25)", overflow: "auto"}}>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{borderBottom: "1px solid #3F464F", paddingBottom: "6px", marginBottom: "10px"}}>
                                            <Typography sx={{color: "#02cfee", fontSize: "1rem", fontWeight: 700, letterSpacing: 0.3, textAlign: "left"}}>
                                                Configured Researchers
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<AddCircleOutlineIcon />}
                                                onClick={handleCreate}
                                                sx={{minWidth: 100, fontSize: "0.75rem"}}
                                            >
                                                Create
                                            </Button>
                                        </Stack>
                                        {researchers.length === 0 ? (
                                            <Stack spacing={2} sx={{alignItems: "center", justifyContent: "center", padding: "48px 24px"}}>
                                                <Box component="img" src="/images/researchers.svg" sx={{width: 96, height: 96, opacity: 0.55}} />
                                                <Typography sx={{color: "rgba(242, 245, 250, 0.6)", fontSize: "0.85rem", textAlign: "center", maxWidth: 280, lineHeight: 1.5}}>
                                                    To get started, follow the prompts from the Create feature above.
                                                </Typography>
                                            </Stack>
                                        ) : (
                                            <>
                                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{marginBottom: "10px", flexWrap: "wrap"}}>
                                                    <StyledSearch>
                                                        <StyledSearchIconWrapper>
                                                            <img src="/images/search_white.svg" width={16} />
                                                        </StyledSearchIconWrapper>
                                                        <StyledInputBase
                                                            placeholder="Filter researchers..."
                                                            value={filterText}
                                                            onChange={(e) => setFilterText(e.target.value)}
                                                        />
                                                    </StyledSearch>
                                                    <Typography sx={appStyles.panelFilterCount}>
                                                        Showing {filtered.length} of {researchers.length}
                                                    </Typography>
                                                </Stack>
                                                {filtered.length === 0 ? (
                                                    <Typography sx={{color: "rgba(242, 245, 250, 0.4)", fontSize: "0.80rem", textAlign: "center", padding: "24px"}}>
                                                        No researchers have been found
                                                    </Typography>
                                                ) : (
                                                    <Stack direction="column" spacing={1} sx={{width: "100%"}}>
                                                        {filtered.map(r => (
                                                            <Stack
                                                                key={r.key}
                                                                sx={researcherCardSx(selectedKey === r.key)}
                                                                onClick={() => setSelectedKey(r.key)}
                                                                spacing={0.5}
                                                            >
                                                                <Typography sx={{fontSize: "0.9rem", fontWeight: 600, color: "#F2F5FA", textAlign: "left"}}>
                                                                    {r.name}
                                                                </Typography>
                                                                <Typography sx={{fontSize: "0.7rem", fontWeight: 300, color: "rgba(242,245,250,0.7)", lineHeight: 1.4, textAlign: "left"}}>
                                                                    {r.description}
                                                                </Typography>
                                                            </Stack>
                                                        ))}
                                                    </Stack>
                                                )}
                                            </>
                                        )}
                                    </Box>
                                )}
                                second={(
                                    <Box sx={{display: "flex", flexDirection: "column", width: "100%", height: "100%", border: "1px solid #3F464F", borderRadius: "8px", padding: "12px", background: "rgba(15, 15, 15, 0.25)", overflow: "auto"}}>
                                        <Box sx={{borderBottom: "1px solid #3F464F", paddingBottom: "6px", marginBottom: "10px"}}>
                                            <Typography sx={{color: "#38ff82", fontSize: "1rem", fontWeight: 700, letterSpacing: 0.3, textAlign: "left"}}>
                                                {selected ? `Results for ${selected.name}` : "Results"}
                                            </Typography>
                                        </Box>
                                        {selected ? (
                                            <Stack spacing={3} sx={{flex: 1, minHeight: 0, alignItems: "center", justifyContent: "center", textAlign: "center", padding: "32px 24px"}}>
                                                <Typography sx={{color: "rgba(242, 245, 250, 0.5)", fontSize: "0.85rem"}}>
                                                    Results preview will load here when the researcher feature is wired up.
                                                </Typography>
                                                <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
                                                    <Button variant="contained" onClick={handleOpenResearcher} sx={{minWidth: 160}}>
                                                        Open Researcher
                                                    </Button>
                                                    <Button variant="contained" onClick={handleOpenResults} sx={{minWidth: 160}}>
                                                        Open Results
                                                    </Button>
                                                </Stack>
                                            </Stack>
                                        ) : (
                                            <Stack sx={{flex: 1, minHeight: 0, alignItems: "center", justifyContent: "center", padding: "48px 24px"}}>
                                                <Typography sx={{color: "rgba(242, 245, 250, 0.4)", fontSize: "0.85rem", textAlign: "center", maxWidth: 320, lineHeight: 1.5}}>
                                                    Select a researcher from the list to preview its results.
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Box>
                                )}
                            />
                        </Box>
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}

export default UXResearcher;
